import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, parseAddressComponents } from "@/lib/gmaps";

/**
 * Address input backed by Google's NEW Places API (2025+):
 * ``AutocompleteSuggestion.fetchAutocompleteSuggestions()``.
 *
 * We deliberately use the low-level programmatic API instead of the
 * ``<gmp-place-autocomplete>`` Web Component because the Web Component:
 *   • takes over the input and blocks manual typing
 *   • forces a full-screen search overlay on mobile
 *   • can't be styled to sit inside a form field group
 *
 * Behavior:
 *   • Always renders a normal ``<input>`` — the customer can type any
 *     address freely, even when Google Places is broken/unreachable.
 *   • When Places is available, we fetch predictions as they type and
 *     render our own dropdown. Clicking a prediction populates the
 *     parent form's city/state/ZIP/lat/lng via ``onAddressSelected``.
 *   • When Places fails (bad key, quota, API not enabled), the input
 *     silently keeps working — the customer types manually and the
 *     parent form's own City/State/ZIP fields collect the data.
 *
 * Props:
 *   value              (str) — controlled input value
 *   onChange           (fn)  — fires on every keystroke: (nextValue) => …
 *   onAddressSelected  (fn)  — fires when a Google suggestion is picked:
 *                              ({line1, city, state, zip, lat, lng}) => …
 *   placeholder        (str)
 *   testid             (str)
 *   onLoadStateChange  (fn)  — "loading" | "ready" | "error"
 */
export default function GoogleAddressInput({
    value = "",
    onChange,
    onAddressSelected,
    placeholder = "Start typing your address...",
    testid = "google-address-input",
    onLoadStateChange,
}) {
    const inputRef = useRef(null);
    // AutocompleteSuggestion + fresh session token per typing session (Google
    // recommends re-using a session token from first keystroke through the
    // final "select" — reduces billing to a single Places search hit).
    const suggestionApiRef = useRef(null);
    const sessionTokenRef = useRef(null);
    const debounceRef = useRef(null);
    const abortRef = useRef({ ignore: false });

    const [ready, setReady] = useState(false);
    const [placesError, setPlacesError] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    // Load Google Maps + Places library once.
    useEffect(() => {
        let mounted = true;
        loadGoogleMaps()
            .then(async (google) => {
                try {
                    const lib = await google.maps.importLibrary("places");
                    if (!mounted) return;
                    if (!lib?.AutocompleteSuggestion) {
                        throw new Error("AutocompleteSuggestion not available on this project");
                    }
                    suggestionApiRef.current = lib;
                    sessionTokenRef.current = new lib.AutocompleteSessionToken();
                    setReady(true);
                    onLoadStateChange?.("ready");
                } catch (err) {
                    console.warn("Places (New) load failed —", err?.message || err);
                    setPlacesError(true);
                    onLoadStateChange?.("error");
                }
            })
            .catch((err) => {
                console.warn("GoogleAddressInput: Maps unavailable —", err?.message || err);
                setPlacesError(true);
                onLoadStateChange?.("error");
            });
        return () => { mounted = false; };
        // eslint-disable-next-line
    }, []);

    // Fetch suggestions with debounce as the user types. Cancels in-flight
    // callbacks via a per-request ``ignore`` flag so stale results don't
    // overwrite fresh ones.
    const fetchSuggestions = (input) => {
        if (!ready || !suggestionApiRef.current) return;
        const trimmed = (input || "").trim();
        if (trimmed.length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        const req = { ignore: false };
        abortRef.current.ignore = true; // cancel any previous inflight
        abortRef.current = req;

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const { AutocompleteSuggestion } = suggestionApiRef.current;
                const { suggestions: results } =
                    await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                        input: trimmed,
                        sessionToken: sessionTokenRef.current,
                        includedRegionCodes: ["us"],
                        includedPrimaryTypes: ["street_address", "premise",
                                                 "subpremise", "route"],
                    });
                if (req.ignore) return;
                // Guard against undefined and non-place predictions (query-only).
                const clean = (results || []).filter(
                    (s) => s.placePrediction,
                );
                setSuggestions(clean);
                setOpen(clean.length > 0);
                setHighlight(-1);
            } catch (err) {
                if (req.ignore) return;
                console.warn("Places suggest failed —", err?.message || err);
                setSuggestions([]);
                setOpen(false);
                // Not fatal — user can still type manually.
            }
        }, 220);
    };

    const handlePick = async (suggestion) => {
        if (!suggestion?.placePrediction) return;
        setOpen(false);
        try {
            const place = suggestion.placePrediction.toPlace();
            await place.fetchFields({
                fields: ["addressComponents", "formattedAddress", "location"],
            });
            const parsed = parseAddressComponents(place);
            // Populate the visible input with a clean formatted street line.
            onChange?.(parsed.line1 || place.formattedAddress || "");
            onAddressSelected?.(parsed);
            // Rotate the session token — the current session is spent.
            sessionTokenRef.current =
                new suggestionApiRef.current.AutocompleteSessionToken();
        } catch (err) {
            console.warn("Places fetchFields failed —", err?.message || err);
        }
    };

    const handleKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            if (highlight >= 0) {
                e.preventDefault();
                handlePick(suggestions[highlight]);
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40"
                placeholder={placeholder}
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange?.(v);
                    fetchSuggestions(v);
                }}
                onFocus={() => {
                    if (suggestions.length > 0) setOpen(true);
                }}
                onBlur={() => {
                    // Delay close so click on a suggestion registers first.
                    setTimeout(() => setOpen(false), 150);
                }}
                onKeyDown={handleKeyDown}
                data-testid={testid}
                autoComplete="street-address"
            />

            {!ready && !placesError && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">
                    Loading…
                </span>
            )}
            {placesError && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-amber-600 pointer-events-none">
                    manual entry
                </span>
            )}

            {open && suggestions.length > 0 && (
                <div
                    className="absolute left-0 right-0 top-full mt-1 z-50 bg-background border border-border rounded-md shadow-lg overflow-hidden"
                    data-testid={`${testid}-suggestions`}
                >
                    {suggestions.map((s, i) => {
                        const p = s.placePrediction;
                        const primary = p?.mainText?.text || p?.text?.text || "";
                        const secondary = p?.secondaryText?.text || "";
                        return (
                            <button
                                key={p?.placeId || i}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handlePick(s)}
                                onMouseEnter={() => setHighlight(i)}
                                className={
                                    "w-full text-left px-3 py-2 text-sm border-b border-border last:border-b-0 " +
                                    (highlight === i ? "bg-secondary" : "hover:bg-secondary/50")
                                }
                                data-testid={`${testid}-suggestion-${i}`}
                            >
                                <div className="font-medium">{primary}</div>
                                {secondary && (
                                    <div className="text-xs text-muted-foreground truncate">{secondary}</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
